import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { hasAnyValue, matchesSource } from '../constants/address-fields';

/**
 * Copies a group of source fields onto their target fields for as long as `enabled` is true, e.g. a
 * "Same as Farm Address" checkbox driving the mailing address inputs.
 *
 * Turning `enabled` off leaves the copied values in place so the user can edit them independently.
 *
 * Must be called from inside a FormProvider.
 *
 * @param {boolean} enabled whether mirroring is currently active
 * @param {object} fieldMap { [targetField]: sourceField }
 */
function useMirroredFields({ enabled, fieldMap }) {
    const { watch, setValue, clearErrors, trigger } = useFormContext();

    const targets = Object.keys(fieldMap);
    const sourceValues = watch(Object.values(fieldMap));

    useEffect(() => {
        if (!enabled) return;
        // calling setValue does not call onChange, so validation has to be run manually
        clearErrors(targets);
        targets.forEach((target, index) => setValue(target, sourceValues[index], { shouldDirty: true }));
        trigger(targets);
        // Searchable selects hold option objects, so compare serialized values rather than references
    }, [enabled, JSON.stringify(sourceValues)]);
}

/**
 * Drives a "same as" toggle field: mirrors `fieldMap` while the toggle is on, and ticks it once on
 * load when the target fields already match their sources. The toggle is presentation only — strip it
 * before submitting.
 *
 * @param {string} name form field holding the toggle's boolean value
 * @param {object} fieldMap { [targetField]: sourceField }
 * @param {object} [matchFieldMap] pairs to consider when deciding whether to tick on load; defaults to
 *   `fieldMap`. Narrow it to exclude pairs the form pre-populates with the same default on both sides,
 *   which would otherwise read as a match on an empty create form.
 * @returns {boolean} whether mirroring is currently active
 *
 * The tick-on-load only holds for forms whose defaultValues are set when this mounts. A host that
 * applies its record with a later `reset()` wipes the toggle in the same commit, so nothing can react
 * to it — such a host must derive the value itself with `matchesSource` before calling `reset()`.
 */
export function useMirrorFieldsToggle({ name, fieldMap, matchFieldMap = fieldMap }) {
    const { watch, setValue } = useFormContext();
    const enabled = !!watch(name);
    const hasDecidedInitialValue = useRef(false);

    const matchFields = [...Object.keys(matchFieldMap), ...Object.values(matchFieldMap)];
    const matchValues = watch(matchFields);

    useEffect(() => {
        if (hasDecidedInitialValue.current) return;
        const values = Object.fromEntries(matchFields.map((field, index) => [field, matchValues[index]]));
        // Edit forms reset their values after the record loads, so hold off until something arrives
        if (!hasAnyValue(values, matchFieldMap)) return;
        hasDecidedInitialValue.current = true;
        if (matchesSource(values, matchFieldMap)) setValue(name, true);
    }, [JSON.stringify(matchValues)]);

    useMirroredFields({ enabled, fieldMap });

    return enabled;
}

export default useMirroredFields;
